import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class FeaturedProductsAddButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.querySelector('.fp-card__add-label');
    this.liveRegion = this.querySelector('[data-fp-live]');
    this.originalLabel = this.label ? this.label.textContent.trim() : 'Add';

    if (this.button) {
      this.button.addEventListener('click', this.handleClick);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
  }

  handleClick = async (event) => {
    event.preventDefault();

    const variantId = this.dataset.variantId;
    if (!variantId || this.classList.contains('is-loading') || this.button.disabled) return;

    this.classList.remove('is-added', 'has-error');
    this.classList.add('is-loading');
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'featured-products-carousel',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      const data = await response.json();

      if (data.status) {
        throw new Error(data.message || 'Unable to add this item to your cart.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });

      this.classList.remove('is-loading');
      this.classList.add('is-added');
      if (this.liveRegion) this.liveRegion.textContent = 'Added to cart';

      window.setTimeout(() => {
        this.classList.remove('is-added');
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }, 1600);
    } catch (error) {
      deferred.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Unable to add this item to your cart.',
          code: 'INVALID',
        })
      );

      this.classList.remove('is-loading');
      this.classList.add('has-error');
      if (this.liveRegion) this.liveRegion.textContent = 'Could not add this item to your cart';

      window.setTimeout(() => {
        this.classList.remove('has-error');
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }, 2200);
    }
  };
}

if (!customElements.get('featured-products-add-button')) {
  customElements.define('featured-products-add-button', FeaturedProductsAddButton);
}
